import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { Ago, SectionCard, SeverityIcon } from "../../components/primitives";
import { TimeSeriesChart } from "../../components/time-series-chart";
import { formatDuration, formatMs, formatMsTick, formatStamp, useNow } from "../../format";
import { SERIES, STATUS } from "../../palette";
import type { LagSummary, MetricSeries, ProcessInfo } from "../../types";
import { LiveChart, type LiveRow } from "./live-chart";
import { FactList, StatTile, SubHeading } from "./stat-tile";
import { HOUR_MS, lagTone, LIMITS } from "./tone";

/** uptime ticking with the shared clock (the runtime section itself polls every 15 s) */
function Uptime({ startedAt }: { startedAt: number }) {
  const now = useNow();
  return <>{formatDuration((now - startedAt) / 1000)}</>;
}

/** Identity of the backend process the admin talks to. */
export function ProcessCard({ process }: { process: ProcessInfo }) {
  const { t } = useLocalization();
  const recentRestart = process.uptime_s < LIMITS.recentRestartS;
  return (
    <SectionCard title={t("monitoring.backend.process.title")} description={t("monitoring.backend.process.description")}>
      <div className="flex flex-col gap-3">
        <FactList
          min="15rem"
          items={[
            { label: t("monitoring.backend.process.instance"), value: <span className="break-all">{process.instance}</span> },
            { label: t("monitoring.backend.process.role"), value: process.role },
            { label: t("monitoring.backend.process.version"), value: process.version || "–" },
            { label: t("monitoring.backend.process.pid"), value: process.pid },
            { label: t("monitoring.backend.process.hostname"), value: <span className="break-all">{process.hostname}</span> },
            { label: t("monitoring.backend.process.python"), value: process.python },
            { label: t("monitoring.backend.process.platform"), value: <span className="break-all">{process.platform}</span> },
            {
              label: t("monitoring.backend.process.started"),
              value: (
                <span>
                  {formatStamp(process.started_at)} · <Ago ts={process.started_at} />
                </span>
              ),
            },
            { label: t("monitoring.backend.process.uptime"), value: <Uptime startedAt={process.started_at} /> },
          ]}
        />
        {recentRestart && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <SeverityIcon severity="info" className="mt-px h-3.5 w-3.5" />
            <span className="min-w-0 break-words">{t("monitoring.backend.process.recentRestart")}</span>
          </p>
        )}
      </div>
    </SectionCard>
  );
}

/** Event loop lag: the probe's live 5 s maxima of the last hour and the per-bucket maxima of the window. */
export function EventLoopCard({ lag, metrics }: { lag: LagSummary; metrics: MetricSeries | undefined }) {
  const { t } = useLocalization();
  const rows = useMemo<LiveRow[]>(() => lag.samples.map(([ts, value]) => ({ ts, lag: value })), [lag.samples]);
  const liveSeries = useMemo(() => [{ key: "lag", label: t("monitoring.backend.loop.lag"), color: SERIES[0] }], [t]);
  const windowSeries = useMemo(
    () =>
      metrics
        ? [{ key: "lag", label: t("monitoring.backend.loop.lag"), color: SERIES[0], values: metrics.maxes["loop.lag_ms"] ?? [], type: "line" as const }]
        : [],
    [metrics, t],
  );
  const references = [
    { y: LIMITS.lagWarnMs, label: t("monitoring.backend.loop.warnLine"), color: STATUS.warning },
    { y: LIMITS.lagNokMs, label: t("monitoring.backend.loop.nokLine"), color: STATUS.critical },
  ];

  return (
    <SectionCard title={t("monitoring.backend.loop.title")} description={t("monitoring.backend.loop.description")}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-3">
          <StatTile label={t("monitoring.backend.loop.current")} value={formatMs(lag.current_ms)} tone={lagTone(lag.current_ms)} />
          <StatTile
            label={t("monitoring.backend.loop.max1m")}
            value={formatMs(lag.max_1m_ms)}
            tone={lagTone(lag.max_1m_ms)}
          />
          <StatTile
            label={t("monitoring.backend.loop.max5m")}
            value={formatMs(lag.max_5m_ms)}
            tone={lagTone(lag.max_5m_ms)}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">{t("monitoring.backend.loop.thresholds")}</p>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-2">
            <SubHeading>{t("monitoring.backend.loop.live")}</SubHeading>
            <LiveChart
              rows={rows}
              series={liveSeries}
              height={170}
              spanMs={HOUR_MS}
              format={formatMs}
              tickFormat={formatMsTick}
              referenceLines={references}
              emptyText={t("monitoring.backend.loop.empty")}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <SubHeading>
              {t("monitoring.backend.loop.window", {
                window: metrics ? t(`monitoring.window.long.${metrics.window}` as TranslationKey) : "",
              })}
            </SubHeading>
            {metrics ? (
              <TimeSeriesChart
                t={metrics.t}
                stepMs={metrics.step_ms}
                window={metrics.window}
                series={windowSeries}
                height={170}
                format={formatMs}
                tickFormat={formatMsTick}
                referenceLines={references}
                emptyText={t("monitoring.backend.loop.empty")}
              />
            ) : (
              <div className="h-[170px]" />
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
